import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { Eye, FileText } from "../../compose/Icon";
import { readingMinutes } from "../../lib/wordCount";
import { PdfPreviewModal } from "./PdfPreviewModal";

/**
 * `<PaneBottomBar>` — the per-pane bottom bar (design §3) for the brief/article
 * docz panes (research gets NONE — the workbench omits the `footer` for it). It
 * shows a LIVE word-count chip (`n/N` for the article when a target is set, else
 * `n`), a click-through detail popup (full counts + est. reading time + an
 * editable target), and the three export controls: Preview PDF (the branded
 * `<iframe>` modal), Download PDF, Download DOCX — all through the Cerebr8 branded
 * export terminal.
 */
export interface PaneBottomBarProps {
  readonly kind: "brief" | "article";
  readonly docId: string;
  readonly title?: string;
  /** Live word count `n` (reader-visible words of the pane's current content). */
  readonly wordCount: number;
  /** Target word count `N` — article only; `undefined` ⇒ the chip shows just `n`. */
  readonly targetWords?: number;
  /** BCP-47 language of the unit (drives the detail-popup phrasing only). */
  readonly lang?: string;
  /** Optional structural counts for the detail popup. */
  readonly charCount?: number;
  readonly sectionCount?: number;
  /** Called when the editor changes the target in the popup (client-local N). */
  readonly onTargetChange?: (n: number | undefined) => void;
  /** Initial detail-popup open state (used by tests / controlled callers). */
  readonly defaultDetailOpen?: boolean;
}

/** ±5% of the target reads as "on target"; outside is over/under. */
function targetTone(n: number, target: number): "ok" | "over" | "under" {
  const delta = n - target;
  if (Math.abs(delta) <= target * 0.05) return "ok";
  return delta > 0 ? "over" : "under";
}

export function PaneBottomBar({
  kind,
  docId,
  title,
  wordCount,
  targetWords,
  lang,
  charCount,
  sectionCount,
  onTargetChange,
  defaultDetailOpen = false,
}: PaneBottomBarProps) {
  const [detailOpen, setDetailOpen] = useState(defaultDetailOpen);
  const [previewOpen, setPreviewOpen] = useState(false);
  const chipWrapRef = useRef<HTMLDivElement>(null);

  const closeDetail = useCallback(() => setDetailOpen(false), []);
  useEffect(() => {
    if (!detailOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!chipWrapRef.current?.contains(e.target as Node)) closeDetail();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDetail();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [detailOpen, closeDetail]);

  const isArticle = kind === "article";
  const hasTarget = isArticle && typeof targetWords === "number";
  const tone = hasTarget ? targetTone(wordCount, targetWords) : "ok";
  const fmt = (n: number) => n.toLocaleString(lang || undefined);
  const delta = hasTarget ? wordCount - targetWords : 0;
  const deltaPct = hasTarget && targetWords > 0 ? (delta / targetWords) * 100 : 0;

  return (
    <div className="wb-pane__foot" data-kind={kind} data-testid={`pane-foot-${kind}`}>
      {/* Word-count chip + its detail popup. */}
      <div className="wb-foot__chipwrap" ref={chipWrapRef}>
        <button
          type="button"
          className="wb-wordcount"
          data-kind={kind}
          data-tone={tone}
          data-testid={`wordcount-${kind}`}
          aria-haspopup="dialog"
          aria-expanded={detailOpen}
          title="Word count"
          onClick={() => setDetailOpen((v) => !v)}
        >
          <span className="wb-wordcount__n">{fmt(wordCount)}</span>
          {hasTarget ? (
            <>
              <span className="wb-wordcount__sep">/</span>
              <span className="wb-wordcount__target">{fmt(targetWords)}</span>
            </>
          ) : null}
          <span className="wb-wordcount__unit">words</span>
        </button>

        {detailOpen ? (
          <div
            className="wb-shandle-pop wb-wordcount-pop"
            role="dialog"
            data-testid={`wordcount-detail-${kind}`}
          >
            <div className="wb-wordcount-pop__row">
              <span>Words</span>
              <strong>{fmt(wordCount)}</strong>
            </div>
            {hasTarget ? (
              <>
                <div className="wb-wordcount-pop__row">
                  <span>Target</span>
                  <strong>{fmt(targetWords)}</strong>
                </div>
                <div className="wb-wordcount-pop__row" data-tone={tone}>
                  <span>Delta</span>
                  <strong>
                    {delta >= 0 ? "+" : ""}
                    {fmt(delta)} ({deltaPct >= 0 ? "+" : ""}
                    {deltaPct.toFixed(1)}%)
                  </strong>
                </div>
              </>
            ) : null}
            {typeof charCount === "number" ? (
              <div className="wb-wordcount-pop__row">
                <span>Characters</span>
                <strong>{fmt(charCount)}</strong>
              </div>
            ) : null}
            {typeof sectionCount === "number" ? (
              <div className="wb-wordcount-pop__row">
                <span>Sections</span>
                <strong>{fmt(sectionCount)}</strong>
              </div>
            ) : null}
            <div className="wb-wordcount-pop__row">
              <span>Reading time</span>
              <strong>~{readingMinutes(wordCount)} min</strong>
            </div>
            {isArticle && onTargetChange ? (
              <label className="wb-wordcount-pop__target">
                <span>Set target</span>
                <input
                  type="number"
                  min={0}
                  step={50}
                  data-testid={`wordcount-target-input-${kind}`}
                  value={typeof targetWords === "number" ? targetWords : ""}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    onTargetChange(v === "" ? undefined : Math.max(0, Number(v)));
                  }}
                />
              </label>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="wb-foot__spacer" />

      {/* Export controls (design §3.2 items 2–4). */}
      <button
        type="button"
        className="wb-icon-btn wb-foot__btn"
        data-testid={`export-preview-${kind}`}
        title="Preview PDF"
        onClick={() => setPreviewOpen(true)}
      >
        <Eye size={15} strokeWidth={1.5} />
        <span>Preview</span>
      </button>
      <a
        className="wb-icon-btn wb-foot__btn"
        data-testid={`export-download-pdf-${kind}`}
        title="Download branded PDF"
        href={api.exportUrl(docId, "pdf", { template: "cerebr8" })}
        download
      >
        <FileText size={15} strokeWidth={1.5} />
        <span>PDF</span>
      </a>
      <a
        className="wb-icon-btn wb-foot__btn"
        data-testid={`export-download-docx-${kind}`}
        title="Download branded Word document"
        href={api.exportUrl(docId, "docx", { template: "cerebr8" })}
        download
      >
        <FileText size={15} strokeWidth={1.5} />
        <span>DOCX</span>
      </a>

      {previewOpen ? (
        <PdfPreviewModal
          docId={docId}
          title={title}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </div>
  );
}
